// Çağrı geçmişi ve kazanç (9.4.10). Kullanıcının terminal çağrılarını salt-okunur
// listeler: yön (giden/gelen), karşı taraf, faturalanan süre ve tutar — arayan
// ödediğini, aranan kazandığını görür. Para/mahsuplaşma sunucuda; bu ekran yalnız
// GET /calls/history okur, canlı çağrı veya para yolunu etkilemez.
import React, { useMemo, useState } from 'react';
import { FlatList, StyleSheet, Text, TouchableOpacity, View } from '../components/rn';
import { useWindowDimensions } from 'react-native';
import ActivityIndicator from '../components/LoadingIndicator';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { CallHistoryItem, CallHistoryFilter } from '../services/api';
import { isCallHistoryAmountEarned } from '../calls/history';
import { formatCallHistoryDuration } from '../calls/historyPresentation';
import { useCallHistory } from '../calls/useCallHistory';
import { fontSize, radius, spacing, ThemeColors } from '../theme';
import { profilePalette } from '../theme/profileSection';
import { useTheme, useThemeMode } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { formatMoney } from '../data/currencies';
import NotificationBellButton from '../components/NotificationBellButton';
import Avatar from '../components/Avatar';
import { floatingDockContentInset, floatingDockScrollInset } from '../components/floatingDockLayout';
import Ionicons from '@expo/vector-icons/Ionicons';
import { isMissedCallForViewer } from '../calls/label';

type Props = {
  token: string;
  /**
   * 7.6 / D-663 — Aramalar bir dock sekmesi: diğer sekmeler gibi geri oku yok,
   * sağ üstte zil var. Batuhan: "Uygundur".
   */
  onNotificationCenter: () => void;
  onOpenUser?: (userId: number) => void;
};

const clock = (total: number) => {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

export default function CallHistoryScreen({ token, onNotificationCenter, onOpenUser }: Props) {
  const colors = useTheme();
  const { isDark } = useThemeMode();
  const insets = useSafeAreaInsets();
  const { width, fontScale } = useWindowDimensions();
  // Büyük erişilebilirlik yazısında saatin sabit genişliği adı ezmesin.
  // Metni küçültmek yerine dar etkin genişlikte alanları alt alta aç.
  const expanded = (width - insets.left - insets.right) / fontScale < 320;
  const { t, lang } = useTranslation();
  const styles = useMemo(() => makeStyles(colors, isDark), [colors, isDark]);
  // 9.4.10 / D-635 — cevapsız sekmesi. Süzgeç sunucuya gidiyor; burada süzmek,
  // son sayfada cevapsız yoksa sekmeyi boş göstermek olurdu.
  const [filter, setFilter] = useState<CallHistoryFilter>('all');
  const { items, loading, refreshing, loadingMore, failed, moreFailed, refresh, retry, loadMore, retryMore } = useCallHistory(token, filter);

  // 9.4.10 / D-644, D-645 — "Cevapsız" yalnız bana gelip açmadığım arama; süre
  // dolduysa da arayan çalarken kapattıysa da. Sohbet kartıyla ve sunucudaki
  // Cevapsız sekmesiyle AYNI kural — tek kaynak `isMissedCallForViewer`.
  function missedForMe(item: CallHistoryItem) {
    return isMissedCallForViewer({ status: item.status, seconds: 0 }, item.direction === 'outgoing');
  }

  // Yön oku (↗/↙) yönü taşıdığından alt satır yalnız sonucu/süreyi gösterir.
  // Ekran okuyucu yönü satırın tam açıklamasından alır.
  function subtitle(item: CallHistoryItem) {
    if (missedForMe(item)) return t('callHistory.missed');
    // Benim yaptığım ve açılmayan arama, sohbetteki kartla aynı sözle.
    if (item.status === 'missed') return t('call.logNoAnswer');
    if (item.status === 'declined') return t('callHistory.declined');
    if (item.status === 'cancelled') return t('callHistory.cancelled');
    return clock(item.billed_seconds);
  }

  function retryNotice(onRetry: () => Promise<void>, testID: string) {
    return (
      <View testID={testID} style={styles.notice}>
        <Text style={styles.noticeText} accessibilityRole="alert">{t('callHistory.failed')}</Text>
        <TouchableOpacity style={styles.retryButton} onPress={onRetry} accessibilityRole="button">
          <Text style={styles.retryText}>{t('common.retry')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  function renderRow({ item, index }: { item: CallHistoryItem; index: number }) {
    const date = new Date(item.created_at);
    // Yerel takvim günü: UTC kesiti, gece yarısında yanlış gün grubuna sokabilir.
    const startsDay = index === 0 || date.toDateString() !== new Date(items[index - 1].created_at).toDateString();
    // Biçimleyiciyi yalnız dile göre önbellekleme: ekran açıkken cihazın saat
    // dilimi değişip liste yenilenirse grup ve etiket aynı güncel dilimi kullanır.
    const day = date.toLocaleDateString(lang, { day: 'numeric', month: 'long', year: 'numeric' });
    const time = date.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
    const name = [item.other.name, item.other.surname].filter(Boolean).join(' ').trim();
    const missed = missedForMe(item);
    const result = subtitle(item);
    const spokenResult = item.status === 'ended'
      ? formatCallHistoryDuration(item.billed_seconds, lang)
      : result;
    const direction = t(item.direction === 'outgoing' ? 'callHistory.outgoing' : 'callHistory.incoming');
    // Ekonomik rol teknik gelen/giden yönünden bağımsızdır (D-264).
    const earned = isCallHistoryAmountEarned(item);
    const money = item.status === 'ended' && item.amount > 0 ? formatMoney(item.amount, item.currency, lang) : null;
    const amountLabel = money ? t(earned ? 'call.earned' : 'call.spent', { amount: money }) : null;

    return (
      <View>
        {startsDay ? (
          <Text testID={`call-history-day-${item.id}`} style={styles.day} accessibilityRole="header">{day}</Text>
        ) : <View style={styles.separator} />}
        <TouchableOpacity
          testID={`call-history-row-${item.id}`}
          style={[styles.row, expanded && styles.expandedRow]}
          disabled={!onOpenUser}
          onPress={() => onOpenUser?.(item.other.id)}
          accessible
          accessibilityRole={onOpenUser ? 'button' : 'text'}
          accessibilityLabel={[name, direction, spokenResult, day, time, amountLabel].filter(Boolean).join(', ')}
          accessibilityLanguage={lang}
          accessibilityHint={onOpenUser ? t('profile.openProfileHint') : undefined}>
          <View style={styles.avatar} accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
            <Avatar photoUrl={item.other.photo} name={name || null} size={42} />
          </View>
          <View testID={`call-history-content-${item.id}`} style={styles.rowContent}>
            <View testID={`call-history-title-${item.id}`} style={[styles.titleRow, expanded && styles.expandedTitle]}>
              <Text testID={`call-history-name-${item.id}`} style={[styles.name, expanded && styles.expandedName]} numberOfLines={expanded ? undefined : 1} ellipsizeMode="tail">{name}</Text>
              <Text testID={`call-history-time-${item.id}`} style={[styles.time, expanded && styles.expandedTime]}>{time}</Text>
            </View>
            <View testID={`call-history-details-${item.id}`} style={[styles.details, expanded && styles.expandedDetails]}>
              <View style={styles.subtitleRow}>
                <Ionicons
                  name={item.direction === 'outgoing' ? 'arrow-up' : 'arrow-down'}
                  size={14}
                  color={missed ? styles.missed.color : colors.gray600}
                  style={styles.dirArrow}
                  accessible={false}
                />
                <Text testID={`call-history-status-${item.id}`} style={[styles.subtitle, missed && styles.missed]}>{result}</Text>
              </View>
              {money && (
                <Text testID={`call-history-amount-${item.id}`} style={[styles.amount, earned ? styles.earned : styles.spent, expanded && styles.expandedAmount]}>
                  {earned ? `+${money}` : money}
                </Text>
              )}
            </View>
          </View>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      {/* Takvim, Mesajlar ve Keşfet'in üst satırıyla aynı: yalnız zil, sağda. */}
      <View style={styles.topBar}>
        <NotificationBellButton onPress={onNotificationCenter} />
      </View>

      <FlatList
        testID="call-history-list"
        data={items}
        keyExtractor={(item) => String(item.id)}
        refreshing={refreshing}
        onRefresh={loading ? undefined : refresh}
        onEndReached={loadMore}
        onEndReachedThreshold={0.4}
        // Son kayıt ve yeniden deneme alanı ortak dock geometrisinin üstüne kaydırılabilsin.
        contentContainerStyle={[styles.list, {
          paddingBottom: insets.bottom + floatingDockContentInset,
        }, items.length === 0 && styles.emptyList]}
        scrollIndicatorInsets={{ bottom: insets.bottom + floatingDockScrollInset }}
        ListHeaderComponent={(
          <View>
            <View style={styles.segmentRow} accessibilityRole="tablist">
              {(['all', 'missed'] as CallHistoryFilter[]).map((secenek) => (
                <TouchableOpacity
                  key={secenek}
                  testID={`call-history-tab-${secenek}`}
                  style={[styles.segmentBtn, filter === secenek && styles.segmentBtnActive]}
                  accessibilityRole="tab"
                  accessibilityState={{ selected: filter === secenek }}
                  onPress={() => setFilter(secenek)}>
                  <Text
                    style={[styles.segmentText, filter === secenek && styles.segmentTextActive]}
                    numberOfLines={2}>
                    {t(secenek === 'all' ? 'callHistory.tabAll' : 'callHistory.tabMissed')}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            {failed && items.length > 0 ? retryNotice(refresh, 'call-history-refresh-error') : null}
          </View>
        )}
        ListFooterComponent={loadingMore ? (
          <View testID="call-history-more-loading" style={styles.notice}><ActivityIndicator color={colors.black} /></View>
        ) : moreFailed ? retryNotice(retryMore, 'call-history-more-error') : null}
        ListEmptyComponent={loading ? (
          <View testID="call-history-initial-loading" style={styles.emptyState}><ActivityIndicator color={colors.black} /></View>
        ) : failed ? retryNotice(retry, 'call-history-initial-error') : (
          <View testID="call-history-empty" style={styles.emptyState}>
            <View style={styles.emptyIcon} accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
              <Ionicons name="call-outline" size={28} color={colors.gray600} />
            </View>
            <Text style={styles.emptyText}>
              {t(filter === 'missed' ? 'callHistory.emptyMissed' : 'callHistory.empty')}
            </Text>
          </View>
        )}
        renderItem={renderRow}
      />
    </View>
  );
}

const makeStyles = (colors: ThemeColors, isDark: boolean) => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.white },
  // Takvimdeki üst satırla aynı ölçü (CalendarScreen `topBar`).
  topBar: {
    minHeight: 52,
    paddingHorizontal: spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: spacing.xs,
  },
  list: { paddingTop: spacing.xs },
  // Takvimdeki sekme çubuğuyla aynı dil: iki ekranda iki ayrı görünüm, aynı
  // işi yapan iki şeyin farklı görünmesi demek olurdu.
  segmentRow: {
    flexDirection: 'row',
    marginHorizontal: spacing.lg,
    marginBottom: spacing.md,
    backgroundColor: colors.gray100,
    borderRadius: radius.control,
    padding: 4,
    gap: 4,
  },
  segmentBtn: { flex: 1, minHeight: 44, paddingVertical: spacing.sm, borderRadius: radius.control, alignItems: 'center', justifyContent: 'center' },
  segmentBtnActive: { backgroundColor: colors.black },
  segmentText: { fontSize: fontSize.md, fontWeight: '600', color: colors.gray600, textAlign: 'center' },
  segmentTextActive: { color: colors.white },
  emptyList: { flexGrow: 1, justifyContent: 'center' },
  emptyState: { alignItems: 'center', gap: spacing.md, paddingHorizontal: spacing.lg },
  emptyIcon: { width: 64, height: 64, borderRadius: 32, backgroundColor: colors.gray100, alignItems: 'center', justifyContent: 'center' },
  emptyText: { fontSize: fontSize.md, color: colors.gray600, textAlign: 'center' },
  notice: { alignItems: 'center', padding: spacing.md, gap: spacing.xs },
  noticeText: { fontSize: fontSize.sm, color: colors.gray600, textAlign: 'center' },
  retryButton: { minHeight: 44, justifyContent: 'center', paddingHorizontal: spacing.lg, paddingVertical: spacing.sm },
  retryText: { fontSize: fontSize.md, color: colors.black, fontWeight: '600' },
  day: { fontSize: fontSize.sm, fontWeight: '600', color: colors.gray600, paddingHorizontal: spacing.md, paddingTop: spacing.md, paddingBottom: spacing.xs },
  separator: { height: StyleSheet.hairlineWidth, backgroundColor: colors.gray200, marginLeft: spacing.md + 42 + 12, marginRight: spacing.md },
  row: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: spacing.md, paddingVertical: 14, gap: 12, minHeight: 72 },
  avatar: { flexShrink: 0 },
  rowContent: { flex: 1, minWidth: 0, gap: spacing.xs },
  titleRow: { flexDirection: 'row', alignItems: 'baseline', gap: spacing.sm },
  name: { flex: 1, minWidth: 0, fontSize: 15, fontWeight: '600', color: colors.black },
  time: { flexShrink: 0, fontSize: fontSize.sm, color: colors.gray600, fontVariant: ['tabular-nums'] },
  expandedRow: { alignItems: 'flex-start' },
  expandedTitle: { flexDirection: 'column', alignItems: 'stretch', gap: spacing.xs },
  expandedName: { flex: 0 },
  expandedTime: { flexShrink: 1 },
  expandedDetails: { flexDirection: 'column', alignItems: 'stretch' },
  expandedAmount: { flexGrow: 0, textAlign: 'left' },
  details: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'baseline', columnGap: spacing.sm, rowGap: spacing.xs },
  subtitleRow: { flexDirection: 'row', alignItems: 'center', flexShrink: 1, maxWidth: '100%' },
  // Ok yukarı/aşağı ikonu 45° döndürülünce ↗ (giden) / ↙ (gelen) olur.
  dirArrow: { transform: [{ rotate: '45deg' }], marginRight: 4 },
  subtitle: { fontSize: fontSize.sm, color: colors.gray600, flexShrink: 1 },
  // Yalnız gelen + cevapsız kırmızı kalır (D-175); ton her zeminde okunur.
  missed: { color: isDark ? '#FF8A80' : '#B42318' },
  amount: { flexGrow: 1, flexShrink: 1, maxWidth: '100%', textAlign: 'right', fontSize: fontSize.sm, fontWeight: '600', fontVariant: ['tabular-nums'] },
  // Kazanılan tutar lacivert: "kazanıldı" aksanı. Cüzdan'daki yeşil "çekilebilir"
  // durumunu ifade ettiğinden onunla karışmaması için bilinçle ayrı bir renk.
  earned: { color: profilePalette(colors).link },
  spent: { color: colors.gray600 },
});
